import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle2, FileText, Users, BarChart3, Receipt, Mail, Truck } from "lucide-react";

const LOGO_URL = "/dispatch-pay-logo.png";

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <header className="bg-[#0B1A3A] border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Dispatch Pay Calculator"
                 className="h-14 sm:h-16 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-white hover:text-amber-400" data-testid="landing-login">Sign in</Link>
            <Link to="/signup">
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="landing-signup">Start free trial</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0B1A3A] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
          {/* Big centered brand logo -- the first thing visitors see */}
          <div className="flex justify-center mb-10" data-testid="landing-hero-logo">
            <img src={LOGO_URL} alt="Dispatch Pay Calculator"
                 className="h-40 sm:h-56 lg:h-64 w-auto object-contain drop-shadow-2xl" />
          </div>
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded">For trucking fleets</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight">
              Driver pay, trip miles, and PDF statements — <span className="text-amber-400">in one clean app.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
              Built for dispatch and payroll desks. Log trips, pull them into a driver's pay period,
              apply per-mile or percentage splits, generate PDF statements with your branding,
              and export to QuickBooks — with an audit trail behind every change.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/signup">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 px-7" data-testid="hero-signup">
                  Start your 14-day free trial
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="px-7 bg-transparent text-white border-slate-500 hover:bg-white/10 hover:text-white" data-testid="hero-login">
                  Sign in
                </Button>
              </Link>
            </div>
            <div className="mt-4 text-xs text-slate-400">
              No credit card for the trial · Cancel anytime from the billing portal
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Users, title: "Single drivers or teams", body: "Per-mile, percentage, flat, team splits with the lead-driver rate. Weekly deductions apply once per statement." },
            { icon: Truck, title: "Trips & dispatch", body: "Fast trip entry, team toggle, fuel surcharge, filters, and status tracking." },
            { icon: FileText, title: "PDF statements", body: "Branded pay stubs with your logo and company header. Include a dispatcher message." },
            { icon: BarChart3, title: "Live reports", body: "Period roll-ups, pay totals, and driver summaries that update as you edit trips." },
            { icon: Receipt, title: "QuickBooks export", body: "One-click CSV in the format QuickBooks expects. Drop it in — done." },
            { icon: Mail, title: "Audit trail", body: "Field-level history on every record so you can defend every number later." },
          ].map((f) => (
            <div key={f.title} className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="w-10 h-10 rounded bg-[#0B1A3A] grid place-items-center text-amber-400 mb-3"><f.icon size={18} /></div>
              <div className="font-semibold text-slate-900">{f.title}</div>
              <div className="text-sm text-slate-600 mt-1">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-white border-y border-slate-200 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Pricing</span>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900">Simple plans. Pick what fits.</h2>
            <p className="mt-3 text-slate-600">
              14-day free trial on every plan. No credit card to start. Cancel anytime from the Stripe portal.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              {
                key: "starter",
                name: "Starter",
                price: 29,
                tagline: "Owner-operators & 1–3 truck fleets",
                features: [
                  "Up to 3 active drivers",
                  "100 trips per month",
                  "Branded PDF pay statements",
                  "Email support",
                ],
                accent: false,
                testid: "pricing-starter",
              },
              {
                key: "growth",
                name: "Growth",
                price: 69,
                tagline: "Small fleets with 3–10 trucks",
                features: [
                  "Up to 10 active drivers",
                  "Unlimited trips",
                  "QuickBooks CSV export",
                  "Custom fields & audit trail",
                  "Email support",
                ],
                accent: true,
                testid: "pricing-growth",
              },
              {
                key: "pro",
                name: "Pro",
                price: 149,
                tagline: "Growing fleets with 10+ trucks",
                features: [
                  "Unlimited drivers & trips",
                  "Everything in Growth",
                  "Driver portal (mobile pay stubs)",
                  "White-label branding",
                  "Priority phone + SMS support",
                ],
                accent: false,
                testid: "pricing-pro",
              },
            ].map((p) => (
              <div
                key={p.key}
                className={`relative rounded-lg bg-slate-50 p-6 flex flex-col
                  ${p.accent ? "border-2 border-amber-500 shadow-md md:scale-[1.03]" : "border border-slate-200"}
                `}
                data-testid={p.testid}
              >
                {p.accent && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-slate-900 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                    Most popular
                  </span>
                )}
                <div className="text-sm font-semibold text-amber-600 uppercase tracking-wider">{p.name}</div>
                <div className="mt-3 flex items-end gap-1">
                  <div className="text-4xl font-bold text-slate-900">${p.price}</div>
                  <div className="text-slate-500 mb-1 text-sm">/ month</div>
                </div>
                <div className="text-xs text-slate-500 mt-1 min-h-[32px]">{p.tagline}</div>
                <ul className="mt-5 space-y-2 text-sm text-slate-700 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="mt-6">
                  <Button
                    className={`w-full ${
                      p.accent
                        ? "bg-amber-500 hover:bg-amber-600 text-slate-900"
                        : "bg-slate-900 hover:bg-slate-800 text-white"
                    }`}
                    data-testid={`${p.testid}-cta`}
                  >
                    Start 14-day free trial
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center text-xs text-slate-500">
            Prices in USD. You can switch between plans anytime from the billing portal.
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B1A3A] text-slate-400">
        <div className="max-w-6xl mx-auto px-6 py-10 text-center text-xs">
          <img src={LOGO_URL} alt="Dispatch Pay Calculator" className="h-10 w-auto mx-auto opacity-90" />
          <div className="mt-3">Trucking payroll solutions for small fleets</div>
          <div className="mt-4 flex justify-center gap-5">
            <Link to="/login" className="hover:text-amber-400">Sign in</Link>
            <Link to="/signup" className="hover:text-amber-400">Create a workspace</Link>
            <Link to="/terms" className="hover:text-amber-400" data-testid="footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-amber-400" data-testid="footer-privacy">Privacy</Link>
            <a href="mailto:cs@dispatchpaycalc.com" className="hover:text-amber-400">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
